import {
  formatDate as formatLocalizedDate,
  type DateInput,
} from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";

/**
 * Date Utilities for Care Calendar
 *
 * Provides consistent date handling across the app using a single authoritative
 * source for "today" and timezone-aware calculations.
 *
 * ## Timezone Strategy
 *
 * Functions accept an optional `timezone` parameter (IANA identifier, e.g.
 * "America/New_York"). When provided, dates are computed relative to that
 * timezone instead of the device's local time. The `useTimezone()` hook
 * in `@src/packages/preferences` resolves the user's preferred timezone
 * from the backend and should be used to obtain this value in components.
 *
 * ## Week Start Convention
 *
 * This module uses Monday as the first day of the week (Monday=0, Sunday=6).
 * This is hardcoded and does NOT rely on browser locale defaults.
 */

/**
 * Get the device's timezone identifier.
 * Falls back to "UTC" if detection fails.
 */
export function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

/**
 * Resolve the current date parts (year, month, day) in a given timezone.
 * Uses Intl.DateTimeFormat to extract components without shifting the date.
 */
function nowInTimezone(tz: string): {
  year: number;
  month: number;
  day: number;
} {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  let year = now.getFullYear();
  let month = now.getMonth();
  let day = now.getDate();

  for (const p of parts) {
    if (p.type === "year") {
      year = parseInt(p.value, 10);
    }
    if (p.type === "month") {
      month = parseInt(p.value, 10) - 1;
    }
    if (p.type === "day") {
      day = parseInt(p.value, 10);
    }
  }

  return { year, month, day };
}

/**
 * Get today's date.
 *
 * When `timezone` is provided, the returned Date is constructed so that
 * its local getFullYear/getMonth/getDate match "today" in that timezone.
 * Without a timezone, falls back to device local time.
 *
 * @param timezone Optional IANA timezone identifier (e.g. "America/New_York")
 */
export function getToday(timezone?: string): Date {
  if (!timezone) {
    return new Date();
  }
  const { year, month, day } = nowInTimezone(timezone);
  return new Date(year, month, day);
}

/**
 * Get the year, month, and day components of today's date.
 *
 * @param timezone Optional IANA timezone identifier
 */
export function getTodayComponents(timezone?: string): {
  year: number;
  month: number;
  day: number;
} {
  if (timezone) {
    return nowInTimezone(timezone);
  }
  const today = new Date();
  return {
    year: today.getFullYear(),
    month: today.getMonth(),
    day: today.getDate(),
  };
}

/**
 * Convert a JavaScript Date.getDay() value (Sunday=0) to Monday-first index (Monday=0).
 *
 * JavaScript's Date.getDay() returns:
 *   Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
 *
 * We need Monday-first for our calendar grid:
 *   Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
 *
 * Conversion formula: (getDay() + 6) % 7
 *   - Sunday (0) → (0+6)%7 = 6 ✓
 *   - Monday (1) → (1+6)%7 = 0 ✓
 *   - Tuesday (2) → (2+6)%7 = 1 ✓
 *   - Wednesday (3) → (3+6)%7 = 2 ✓
 *   - Thursday (4) → (4+6)%7 = 3 ✓
 *   - Friday (5) → (5+6)%7 = 4 ✓
 *   - Saturday (6) → (6+6)%7 = 5 ✓
 */
export function toMondayFirstIndex(jsDayOfWeek: number): number {
  return (jsDayOfWeek + 6) % 7;
}

/**
 * Get the Monday-first day index for a given date.
 *
 * @param date The date to get the weekday for
 * @returns Monday=0, Tuesday=1, ..., Sunday=6
 */
export function getMondayFirstDayOfWeek(date: Date): number {
  return toMondayFirstIndex(date.getDay());
}

/**
 * Generate calendar days for a given month with proper Monday-based week alignment.
 *
 * Returns an array of day numbers (1-31) and null placeholders for empty cells.
 * The array is structured for a 7-column grid (Monday → Sunday) with:
 * - Leading nulls to align day 1 under the correct weekday
 * - Trailing nulls to complete the final row
 *
 * @param year The year (e.g., 2026)
 * @param month The month (0-indexed: January=0, December=11)
 * @returns Array of (number | null) representing the calendar grid
 */
export function generateCalendarDays(
  year: number,
  month: number,
): (number | null)[] {
  const days: (number | null)[] = [];

  // Get the first day of the month
  const firstDay = new Date(year, month, 1);

  // Convert to Monday-based index (Monday=0, Sunday=6)
  const startDayIndex = getMondayFirstDayOfWeek(firstDay);

  // Get number of days in the month
  // (Day 0 of next month = last day of current month)
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Add leading empty placeholders for days before the 1st
  for (let i = 0; i < startDayIndex; i++) {
    days.push(null);
  }

  // Add all days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  // Add trailing empty placeholders to complete the final row (7 columns)
  const remainder = days.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) {
      days.push(null);
    }
  }

  return days;
}

/**
 * Check if a given day number is "today" within a specific month/year context.
 *
 * @param day The day number to check (1-31) or null
 * @param displayedMonth The month being displayed (0-indexed)
 * @param displayedYear The year being displayed
 * @returns true if the day is today
 */
export function isDayToday(
  day: number | null,
  displayedMonth: number,
  displayedYear: number,
  timezone?: string,
): boolean {
  if (day === null) return false;

  const { year, month, day: todayDay } = getTodayComponents(timezone);
  return day === todayDay && displayedMonth === month && displayedYear === year;
}

/**
 * Format a month and year for display (e.g., "January 2026").
 * Uses English month names explicitly to avoid locale-dependent formatting.
 */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function formatMonthYear(
  year: number,
  month: number,
  locale?: Locale,
): string {
  if (locale) {
    return formatLocalizedDate(new Date(year, month, 1), locale, {
      year: "numeric",
      month: "long",
    });
  }
  return `${MONTH_NAMES[month]} ${year}`;
}

/**
 * Format a date for daily view display (e.g., "Monday, Jan 19").
 */
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_ABBREVS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatDailyDate(date: Date, locale?: Locale): string {
  if (locale) {
    return formatLocalizedDate(date as DateInput, locale, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }
  return `${DAY_NAMES[date.getDay()]}, ${MONTH_ABBREVS[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Weekday labels for calendar header (Monday-first order).
 */
export const WEEKDAY_LABELS = [
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
  "SUN",
] as const;

/**
 * Convert a Date to a local date string in YYYY-MM-DD format.
 *
 * IMPORTANT: This uses local timezone components (getFullYear, getMonth, getDate)
 * instead of toISOString() which converts to UTC and can shift the day.
 *
 * Example: If local time is Jan 19, 2026 at 8 PM PST (UTC-8):
 *   - toISOString() would return "2026-01-20T04:00:00.000Z" → split gives "2026-01-20" (WRONG!)
 *   - toLocalDateString() returns "2026-01-19" (CORRECT!)
 *
 * @param date The date to format
 * @returns Date string in YYYY-MM-DD format using local timezone
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD date string as local midnight.
 *
 * IMPORTANT: `new Date("2026-03-15")` parses as UTC midnight, which shifts
 * the date backward in western timezones. This function always parses as
 * local midnight, consistent with `toLocalDateString`.
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Date object at local midnight for the given date
 */
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year!, month! - 1, day!);
}

/**
 * Create a local date string from year, month, day components.
 *
 * @param year The year (e.g., 2026)
 * @param month The month (0-indexed: January=0, December=11)
 * @param day The day of month (1-31)
 * @returns Date string in YYYY-MM-DD format
 */
export function createLocalDateString(
  year: number,
  month: number,
  day: number,
): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Get today's date as a YYYY-MM-DD string.
 *
 * @param timezone Optional IANA timezone identifier
 */
export function getTodayString(timezone?: string): string {
  if (timezone) {
    const { year, month, day } = nowInTimezone(timezone);
    return createLocalDateString(year, month, day);
  }
  return toLocalDateString(getToday());
}
